// Skills v2 — on-the-agent UX pure formatting logic (2026-08-19).
//
// Zero React. Every user-facing string is outcome-first, no jargon
// (the mockup is the conformance spec: docs/design/skills-framework-v2-mockup.html).
// All logic that shapes the SkillsSection lives here so it's unit-testable
// to 100% and the component stays a thin renderer.
//
// Backend contracts:
//   suggestions()   → [{skill_name, when_to_use, score, action, trigger,
//                       reason, attached}]  trigger ∈ data|semantic|explicit
//   capabilities()  → {satisfied, requires, statuses:[{token, kind,
//                       satisfied, reason, detail:{provider, field, tab,
//                       card, message}}], missing}
//   previewImpact() → {baseline_accuracy, with_skill_accuracy, delta,
//                       scored, errored, examples:[...]}  (0..1 fractions|null)

// Split a suggestions array into the two mockup sections. Explicit/attached
// skills are "Active on this agent"; everything else is "Suggested". Order is
// preserved within each bucket (the backend already sorts explicit first).
export function splitSuggestions(suggestions) {
  const list = Array.isArray(suggestions) ? suggestions : [];
  const active = [];
  const suggested = [];
  for (const item of list) {
    if (item && item.attached === true) active.push(item);
    else suggested.push(item);
  }
  return { active, suggested };
}

// Health pill for an active skill. The suggestions endpoint carries no health
// field today, so a live-attached skill defaults to Healthy. The
// `needs_review` branch is defensive/forward-compat for when the endpoint
// starts surfacing decayed-lift health inline.
export function healthOf(item) {
  if (item && item.health === "needs_review") {
    return { label: "Needs a look", tone: "warn" };
  }
  return { label: "Healthy", tone: "ok" };
}

// The plain-English "why suggested" line. Falls back to a generic, still
// jargon-free sentence when the backend didn't attach a reason.
export function whyText(item) {
  const reason = item && typeof item.reason === "string" ? item.reason.trim() : "";
  if (reason) return reason;
  return "This skill fits what this agent already does.";
}

// Human label for the trigger that surfaced a suggestion.
export function triggerLabel(trigger) {
  if (trigger === "data") return "Matches your data";
  if (trigger === "semantic") return "Fits this agent";
  if (trigger === "explicit") return "Attached";
  return "";
}

// Turn a capability token like "mcp:slack" or "communication__send_email"
// into a friendly label ("Slack", "Send email"). Best-effort + defensive:
// strips a leading "kind:" prefix, splits on the toolset "__" separator,
// replaces separators with spaces, and title-cases the first word.
export function humanToken(token) {
  if (!token || typeof token !== "string") return "";
  let t = token.trim();
  if (!t) return "";
  // Drop a leading "mcp:" / "tool:" style scheme prefix.
  const colon = t.indexOf(":");
  if (colon !== -1) t = t.slice(colon + 1);
  // Prefer the tool name after the toolset "__" separator.
  if (t.includes("__")) t = t.split("__").pop();
  t = t.replace(/[_-]+/g, " ").trim();
  if (!t) return "";
  return t.charAt(0).toUpperCase() + t.slice(1);
}

// The "Uses:" chip row for a suggested skill. Satisfied capabilities render as
// a calm green check; unsatisfied ones become an actionable
// "⚠ Needs <provider> · Connect" chip that deep-links to the Wiring tab.
export function capabilityChips(capabilities) {
  const statuses = capabilities && Array.isArray(capabilities.statuses)
    ? capabilities.statuses
    : [];
  return statuses.map((status) => {
    const detail = (status && status.detail) || {};
    if (status && status.satisfied) {
      return { label: `✓ ${humanToken(status.token)}`, tone: "ok", deepLink: null };
    }
    const provider = detail.provider || humanToken(status && status.token) || "a connection";
    return {
      label: `⚠ Needs ${provider} · Connect`,
      tone: "warn",
      deepLink: { tab: detail.tab || "wiring", card: detail.card || null },
    };
  });
}

// 0.71 → "71%"; null/undefined/non-number → "—".
export function formatPct(fraction) {
  if (typeof fraction !== "number" || Number.isNaN(fraction)) return "—";
  return `${Math.round(fraction * 100)}%`;
}

// 0.18 → "▲ 18%"; -0.09 → "▼ 9%"; 0 → "no change"; null → "—".
export function formatDelta(delta) {
  if (typeof delta !== "number" || Number.isNaN(delta)) return "—";
  const pct = Math.round(Math.abs(delta) * 100);
  if (delta > 0) return `▲ ${pct}%`;
  if (delta < 0) return `▼ ${pct}%`;
  return "no change";
}

// Tone → hex, with the memory-rule fallbacks (no bare COLORS.red).
export function toneColor(tone, colors) {
  const c = colors || {};
  if (tone === "warn") return c.orange || "#f59e0b";
  return c.green || "#10b981";
}
